import { I18n } from "@assets/strings";
import { Range } from "immutable";
import React from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { config } from "@core/config/config";
import { Path, searchParams } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { CountryCode } from "@domain/currentProfile/countryCode";
import { allContentLicenses, ContentLicense } from "@domain/licence/contentLicense";
import { allCategories, Category } from "@domain/lms/category/category";
import { allContentLanguages, ContentLanguage } from "@domain/lms/contentLanguage";
import { allContentLevels, ContentLevel } from "@domain/lms/contentLevel";
import { allCourseDurations, allCourseProviders, CourseDuration, CourseProvider } from "@domain/lms/course/course";
import { OrganizationSubType, OrganizationType, subTypesByTypes } from "@domain/organization/organizationType";
import { allScoring, Scoring } from "@domain/ranking/ranking";
import { allSearchFiltersWithHeaders, SearchDomain, searchFiltersHeaders } from "@domain/search/searchDomain";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { StarIcon } from "@ui/components/icons";
import { RadioButton } from "@ui/components/inputs/radio";
import { Row, Stack } from "@ui/components/layout";
import { CountriesFilter } from "@ui/components/search/countriesFilter";
import { StickyColumn } from "@ui/components/stickyColumn";
import { theme } from "@ui/styles/theme";
import { gap } from "@ui/styles/utils";

interface SearchFiltersProps extends React.HTMLAttributes<HTMLDivElement> {
	query: string;
	domain?: SearchDomain;
	categories?: Category[];
	levels?: ContentLevel[];
	languages?: ContentLanguage[];
	durations?: CourseDuration[];
	providers?: CourseProvider[];
	orgTypes?: OrganizationType[];
	orgSubTypes?: OrganizationSubType[];
	countries?: CountryCode[];
	license?: ContentLicense[];
	scoring?: Scoring[];
	rating?: number;
}
export const SearchFilters: React.FC<SearchFiltersProps> = ({
	query,
	domain,
	categories,
	levels,
	languages,
	durations,
	providers,
	orgTypes,
	orgSubTypes,
	countries,
	license,
	scoring,
	rating,
	...htmlProps
}) => {
	const history = useHistory();
	const { trackingService } = useServices();
	const selectedDomain =
		domain === SearchDomain.Organization && orgTypes && orgTypes.length === 1 ? orgTypes[0] : domain;
	function addParam<T>(newValue: T, list?: T[]): T[] {
		return [...(list || []), newValue] as T[];
	}

	function removeParam<T>(value: T, list?: T[]): T[] | undefined {
		if (!list || list.length === 1) {
			return undefined;
		}
		return list.filter((v) => v !== value);
	}

	return (
		<Container {...htmlProps} topOffset={120} bottomOffset={20} onClick={(e) => e.stopPropagation()}>
			<Stack gap={13}>
				<PrimaryMedium>{I18n.t("search.filter.type.title")}</PrimaryMedium>
				<Filter>
					<RadioButton
						id={"all-domains"}
						name="all-domains"
						label={I18n.t("search.filter.type.all")}
						labelStyle={theme.textStyles.light}
						checked={!selectedDomain}
						onChange={() =>
							history.push({
								pathname: Path.Search,
								search: searchParams(query, undefined, categories, levels, languages, durations, providers),
							})
						}
					/>
					{allSearchFiltersWithHeaders
						.filter((domain: string) => domain !== SearchDomain.Shareroom || config.features.shareRooms)
						.map((domain: string) =>
							!searchFiltersHeaders.includes(domain) ? (
								<RadioButton
									key={domain}
									id={domain}
									name="domain"
									label={I18n.t(`search.filter.type.${domain}`)}
									labelStyle={theme.textStyles.light}
									checked={domain === selectedDomain}
									onChange={() =>
										history.push({
											pathname: Path.Search,
											search: searchParams(
												query,
												domain === "INSTITUTE" || domain === "COMPANY"
													? SearchDomain.Organization
													: (domain as SearchDomain),
												categories,
												domain === SearchDomain.Course || domain === SearchDomain.Quiz ? levels : undefined,
												domain === SearchDomain.Course || domain === SearchDomain.User ? languages : undefined,
												domain === SearchDomain.Course ? durations : undefined,
												domain === SearchDomain.Course ? providers : undefined,
												domain === "INSTITUTE" || domain === "COMPANY" ? [domain] : undefined
											),
										})
									}
								/>
							) : (
								<h6 key={domain} style={{ fontSize: 11.5 }}>
									{I18n.t(`search.filter.header.${domain}`)}
								</h6>
							)
						)}
				</Filter>
			</Stack>
			{selectedDomain && selectedDomain === SearchDomain.Shareroom && (
				<Stack gap={13}>
					{config.features.classrooms.enablePremium ? (
						<>
							<PrimaryMedium>{I18n.t("search.filter.license.title")}</PrimaryMedium>
							<Filter>
								{allContentLicenses.map((option) => {
									const isChecked = license?.includes(option);
									return (
										<RadioButton
											key={option}
											id={option}
											name="license"
											label={I18n.t(`search.filter.license.${option}`)}
											labelStyle={theme.textStyles.light}
											checked={isChecked ?? false}
											onChange={() => {
												trackingService.track(
													option === "FREE"
														? "Page View / Explorer / Licence Free"
														: "Page View / Explorer / Licence Paid"
												);
												history.push({
													pathname: Path.Search,
													search: searchParams(
														query,
														selectedDomain,
														categories,
														levels,
														languages,
														durations,
														providers,
														orgTypes,
														orgSubTypes,
														countries,
														isChecked ? removeParam(option) : addParam(option),
														scoring,
														rating
													),
												});
											}}
										/>
									);
								})}
							</Filter>
						</>
					) : null}
					<PrimaryMedium>{I18n.t("search.filter.scoring.title")}</PrimaryMedium>
					<Filter>
						{allScoring.map((option) => {
							const isChecked = scoring?.includes(option);
							return (
								<RadioButton
									key={option}
									id={option}
									name="scoring"
									label={I18n.t(`search.filter.scoring.${option}`)}
									labelStyle={theme.textStyles.light}
									checked={isChecked ?? false}
									onChange={() => {
										history.push({
											pathname: Path.Search,
											search: searchParams(
												query,
												selectedDomain,
												categories,
												levels,
												languages,
												durations,
												providers,
												orgTypes,
												orgSubTypes,
												countries,
												license,
												isChecked ? removeParam(option) : addParam(option),
												rating
											),
										});
									}}
								/>
							);
						})}
					</Filter>
					<PrimaryMedium>{I18n.t("search.filter.rating.title")}</PrimaryMedium>
					<Filter>
						<Stack gap={15}>
							{Range(1, 6).map((note) => (
								<Row key={note} align="center" gap={8}>
									<RadioButton
										key={note}
										id={note.toString()}
										name="rating"
										labelStyle={theme.textStyles.light}
										onChange={() => {
											history.push({
												pathname: Path.Search,
												search: searchParams(
													query,
													selectedDomain,
													categories,
													levels,
													languages,
													durations,
													providers,
													orgTypes,
													orgSubTypes,
													countries,
													license,
													scoring,
													note
												),
											});
										}}
									/>
									<Row>
										{Range(1, 6).map((n) => (
											<Star key={n} $isFilled={note >= n} />
										))}
									</Row>
								</Row>
							))}
						</Stack>
					</Filter>
				</Stack>
			)}

			{selectedDomain === "USER" || selectedDomain === "INSTITUTE" || selectedDomain === "COMPANY" ? (
				<Stack gap={13}>
					<PrimaryMedium>{I18n.t("search.filter.user_country.title")}</PrimaryMedium>
					<Filter>
						<CountriesFilter
							initialCountries={countries}
							exportSelectedCountries={(countries) => {
								history.push({
									pathname: Path.Search,
									search: searchParams(
										query,
										domain === "USER" ? SearchDomain.User : (domain as SearchDomain),
										categories,
										levels,
										languages,
										durations,
										providers,
										selectedDomain === "USER" ? undefined : [selectedDomain],
										orgSubTypes,
										countries
									),
								});
							}}
						/>
					</Filter>
				</Stack>
			) : null}
			{selectedDomain === "INSTITUTE" || selectedDomain === "COMPANY" ? (
				<Stack gap={13}>
					<PrimaryMedium>{I18n.t("search.filter.org_type.title")}</PrimaryMedium>
					<Filter>
						{subTypesByTypes[selectedDomain].map((type) => {
							const isChecked = orgSubTypes?.includes(type);
							return (
								<RadioButton
									key={type}
									id={type}
									checkbox
									name="organizationSubType"
									label={I18n.t(`search.filter.org_subtype.${type}`)}
									labelStyle={theme.textStyles.light}
									checked={isChecked ?? false}
									onChange={() =>
										history.push({
											pathname: Path.Search,
											search: searchParams(
												query,
												SearchDomain.Organization,
												categories,
												levels,
												languages,
												durations,
												providers,
												[selectedDomain],
												isChecked ? removeParam(type, orgSubTypes) : addParam(type, orgSubTypes)
											),
										})
									}
								/>
							);
						})}
					</Filter>
				</Stack>
			) : null}
			<Stack gap={13}>
				<PrimaryMedium>{I18n.t("search.filter.category.title")}</PrimaryMedium>
				<Filter>
					{allCategories.map((category) => {
						const isChecked = !!categories?.includes(category);
						return (
							<RadioButton
								key={category}
								id={category}
								checkbox
								name="categories"
								label={I18n.t(`category.${category}`)}
								labelStyle={theme.textStyles.light}
								checked={isChecked}
								onChange={() =>
									history.push({
										pathname: Path.Search,
										search: searchParams(
											query,
											orgTypes ? SearchDomain.Organization : (selectedDomain as SearchDomain),
											isChecked ? removeParam(category, categories) : addParam(category, categories),
											levels,
											languages,
											durations,
											providers,
											orgTypes,
											orgSubTypes,
											countries,
											license
										),
									})
								}
							/>
						);
					})}
				</Filter>
			</Stack>
			{selectedDomain && (selectedDomain === SearchDomain.Course || selectedDomain === SearchDomain.Quiz) && (
				<Stack gap={13}>
					<PrimaryMedium>{I18n.t("search.filter.level.title")}</PrimaryMedium>
					<Filter>
						{allContentLevels.map((level) => {
							const isChecked = levels?.includes(level);
							return (
								<RadioButton
									key={level}
									id={level}
									checkbox
									name="levels"
									label={
										selectedDomain === SearchDomain.Course ? I18n.t(`level.${level}`) : I18n.t(`quiz.level.${level}`)
									}
									labelStyle={theme.textStyles.light}
									checked={isChecked ?? false}
									onChange={() =>
										history.push({
											pathname: Path.Search,
											search: searchParams(
												query,
												selectedDomain,
												categories,
												isChecked ? removeParam(level, levels) : addParam(level, levels),
												languages,
												durations,
												providers
											),
										})
									}
								/>
							);
						})}
					</Filter>
				</Stack>
			)}
			{selectedDomain && selectedDomain === SearchDomain.Course && (
				<Stack gap={13}>
					<PrimaryMedium>{I18n.t("search.filter.duration.title")}</PrimaryMedium>
					<Filter>
						{allCourseDurations.map((duration) => {
							const isChecked = durations?.includes(duration);
							return (
								<RadioButton
									key={duration}
									id={duration}
									checkbox
									name="durations"
									label={I18n.t(`search.filter.duration.${duration}`)}
									labelStyle={theme.textStyles.light}
									checked={isChecked ?? false}
									onChange={() =>
										history.push({
											pathname: Path.Search,
											search: searchParams(
												query,
												selectedDomain,
												categories,
												levels,
												languages,
												isChecked ? removeParam(duration, durations) : addParam(duration, durations),
												providers
											),
										})
									}
								/>
							);
						})}
					</Filter>
				</Stack>
			)}
			{selectedDomain &&
				(selectedDomain === SearchDomain.Course ||
					selectedDomain === SearchDomain.Article ||
					selectedDomain === SearchDomain.Carousel) && (
					<Stack gap={13}>
						<PrimaryMedium>{I18n.t("search.filter.language.title")}</PrimaryMedium>
						<Filter>
							{allContentLanguages.map((language) => {
								const isChecked = languages?.includes(language);
								return (
									<RadioButton
										key={language}
										id={language}
										checkbox
										name="languages"
										label={I18n.t(`language.${language}`)}
										labelStyle={theme.textStyles.light}
										checked={isChecked ?? false}
										onChange={() =>
											history.push({
												pathname: Path.Search,
												search: searchParams(
													query,
													selectedDomain,
													categories,
													levels,
													isChecked ? removeParam(language, languages) : addParam(language, languages),
													durations,
													providers
												),
											})
										}
									/>
								);
							})}
						</Filter>
					</Stack>
				)}
			{selectedDomain && selectedDomain === SearchDomain.Course && config.features.coursesExternalProviders && (
				<Stack gap={13}>
					<PrimaryMedium>{I18n.t("search.filter.provider.title")}</PrimaryMedium>
					<Filter>
						{allCourseProviders.map((provider) => {
							const isChecked = providers?.includes(provider);
							return (
								<RadioButton
									key={provider}
									id={provider}
									checkbox
									name="providers"
									label={provider[0] + provider.substr(1).toLowerCase()}
									labelStyle={theme.textStyles.light}
									checked={isChecked ?? false}
									onChange={() =>
										history.push({
											pathname: Path.Search,
											search: searchParams(
												query,
												selectedDomain,
												categories,
												levels,
												languages,
												durations,
												isChecked ? removeParam(provider, providers) : addParam(provider, providers)
											),
										})
									}
								/>
							);
						})}
					</Filter>
				</Stack>
			)}
		</Container>
	);
};

const Container = styled(StickyColumn)`
	${gap(20)};
`;

const Filter = styled(RoundedBlock)`
	width: 100%;
	padding: 20px;
	${gap(7)};
`;

const Star = styled(StarIcon)<{ $isFilled?: boolean }>`
	width: 20px;
	height: 20px;
	path {
		${({ theme, $isFilled }) => !$isFilled && `fill: ${theme.colors.lightergray}`}
	}
`;
